import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { School, LogIn, Camera, Clock, PlayCircle, PauseCircle, Award, RotateCcw, CheckCircle2, XCircle, ShieldCheck, Loader2, ChevronDown, FileText, Video, SkipBack, SkipForward, BookOpen } from "lucide-react";
import { escolaApi, setEscolaToken, clearEscolaToken, certificateUrl } from "@/lib/escola";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

const fmt = (s) => {
  s = Math.max(0, Math.floor(s || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(x).padStart(2, "0")}`;
};

const IS_MOBILE = typeof navigator !== "undefined" && (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 1 && window.innerWidth < 1024));

const parseVid = (url) => {
  if (!url) return null;
  const m = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

const loadYT = () => new Promise((res) => {
  if (window.YT && window.YT.Player) return res();
  window.onYouTubeIframeAPIReady = () => res();
  if (!document.getElementById("yt-iframe-api")) {
    const s = document.createElement("script"); s.id = "yt-iframe-api"; s.src = "https://www.youtube.com/iframe_api"; document.body.appendChild(s);
  }
});

function Shell({ children, wide }) {
  return (
    <div className="min-h-screen bg-[#081325] text-white">
      <header className="flex items-center gap-3 border-b border-white/10 bg-[#0A1C33] px-5 py-4">
        <img src="/valbrac-logo-3d.png" alt="Valbrac" className="h-10 w-10" />
        <div><p className="text-sm font-extrabold tracking-tight">VALBRAC · Escola Online</p><p className="text-[10px] uppercase tracking-wider text-emerald-400">Treinamentos SSMA (EAD)</p></div>
      </header>
      <main className={`mx-auto px-4 py-8 ${wide ? "max-w-[1400px]" : "max-w-4xl"}`}>{children}</main>
    </div>
  );
}

function LoginStep({ onLogged }) {
  const [u, setU] = useState(""); const [p, setP] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await escolaApi.post("/escola/login", { username: u, password: p });
      setEscolaToken(data.token); onLogged(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Falha no login"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-sm rounded-2xl bg-white p-6 text-slate-800 shadow-xl" data-testid="escola-login">
      <div className="mb-4 flex items-center gap-2 text-[#0B2545]"><LogIn className="h-5 w-5" /><h2 className="text-lg font-bold">Acesso ao treinamento</h2></div>
      <p className="mb-4 text-xs text-slate-500">Etapa 1 de 2 — use o login e a senha do seu treinamento.</p>
      <form onSubmit={submit} className="space-y-3">
        <div><Label className="mb-1 block text-xs font-semibold">Login</Label><Input value={u} onChange={(e) => setU(e.target.value)} data-testid="escola-username" autoFocus /></div>
        <div><Label className="mb-1 block text-xs font-semibold">Senha</Label><Input type="password" value={p} onChange={(e) => setP(e.target.value)} data-testid="escola-password" /></div>
        <Button type="submit" disabled={busy} className="w-full bg-[#0B2545] hover:bg-[#134074]" data-testid="escola-login-btn">{busy ? "Entrando..." : "Entrar"}</Button>
      </form>
    </div>
  );
}

function FaceStep({ info, onDone }) {
  const videoRef = useRef(null); const streamRef = useRef(null);
  const [ready, setReady] = useState(false); const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(""); const [starting, setStarting] = useState(false);

  const stop = useCallback(() => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; }, []);

  const startCamera = useCallback(async () => {
    setErr(""); setStarting(true); setReady(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      setErr("Seu navegador não permite acesso à câmera. Abra pelo Chrome ou Safari atualizado."); setStarting(false); return;
    }
    stop();
    const tries = [{ video: { facingMode: "user" } }, { video: { facingMode: "environment" } }, { video: true }];
    for (const c of tries) {
      try {
        const st = await navigator.mediaDevices.getUserMedia(c);
        streamRef.current = st;
        if (videoRef.current) { videoRef.current.srcObject = st; await videoRef.current.play().catch(() => {}); }
        setReady(true); setStarting(false); return;
      } catch (e) { if (e?.name === "NotAllowedError") break; }
    }
    setErr("Não foi possível acessar a câmera. Toque em “Permitir” quando o navegador pedir e tente novamente. No celular, verifique as permissões de câmera do site.");
    setStarting(false);
  }, [stop]);

  useEffect(() => { startCamera(); return () => stop(); }, [startCamera, stop]);

  const capture = async () => {
    setBusy(true);
    try {
      const v = videoRef.current; const c = document.createElement("canvas");
      c.width = v.videoWidth || 480; c.height = v.videoHeight || 360;
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.8));
      const fd = new FormData(); fd.append("file", blob, "face.jpg");
      const { data } = await escolaApi.post("/escola/face", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEscolaToken(data.token); stop(); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao registrar a foto"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-md rounded-2xl bg-white p-6 text-slate-800 shadow-xl" data-testid="escola-face">
      <div className="mb-2 flex items-center gap-2 text-[#0B2545]"><Camera className="h-5 w-5" /><h2 className="text-lg font-bold">Reconhecimento facial</h2></div>
      <p className="mb-3 text-xs text-slate-500">Etapa 2 de 2 — olá, <b>{info?.employee_name}</b>. Pelo computador ou celular, posicione seu rosto e capture a foto de presença para iniciar o curso <b>{info?.nr}</b>.</p>
      <video ref={videoRef} playsInline autoPlay muted className={`mb-3 w-full rounded-xl border bg-slate-900 ${err ? "hidden" : ""}`} style={{ aspectRatio: "4/3" }} />
      {err ? (
        <>
          <p className="mb-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-600" data-testid="escola-face-error">{err}</p>
          <Button onClick={startCamera} disabled={starting} className="w-full bg-[#0B2545] hover:bg-[#134074]" data-testid="escola-face-retry">
            <Camera className="mr-2 h-4 w-4" /> {starting ? "Abrindo câmera..." : "Tentar novamente"}
          </Button>
        </>
      ) : (
        <Button onClick={capture} disabled={!ready || busy} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="escola-face-capture">{busy ? "Enviando..." : (ready ? "Capturar e iniciar" : "Abrindo câmera...")}</Button>
      )}
    </div>
  );
}

function ExamStep({ course, onResult }) {
  const [ans, setAns] = useState({}); const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (Object.keys(ans).length < course.questions.length) { toast.error("Responda todas as questões"); return; }
    setBusy(true);
    try {
      const answers = course.questions.map((_, i) => ans[i]);
      const { data } = await escolaApi.post("/escola/exam", { answers });
      onResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao enviar prova"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-white p-6 text-slate-800 shadow-xl" data-testid="escola-exam">
      <h2 className="mb-1 text-lg font-bold text-[#0B2545]">Prova final — {course.nr}</h2>
      <p className="mb-5 text-xs text-slate-500">Marque a alternativa correta. Você precisa de {course.pass_score} de {course.questions.length} acertos.</p>
      <div className="space-y-5">
        {course.questions.map((q, i) => (
          <div key={q.id} data-testid={`exam-q-${i}`}>
            <p className="mb-2 text-sm font-semibold">{i + 1}. {q.text}</p>
            <RadioGroup value={ans[i] !== undefined ? String(ans[i]) : ""} onValueChange={(v) => setAns((p) => ({ ...p, [i]: Number(v) }))}>
              {q.options.map((o, oi) => (
                <label key={oi} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 p-2 text-sm hover:bg-slate-50" data-testid={`exam-q-${i}-opt-${oi}`}>
                  <RadioGroupItem value={String(oi)} /> {o}
                </label>
              ))}
            </RadioGroup>
          </div>
        ))}
      </div>
      <Button onClick={submit} disabled={busy} className="mt-6 w-full bg-[#0B2545] hover:bg-[#134074]" data-testid="exam-submit">{busy ? "Enviando..." : "Enviar prova"}</Button>
    </div>
  );
}

function CourseStep({ data, reload }) {
  const { course, progress, enrollment } = data;
  const lessons = course.modules || [];
  const [accrued, setAccrued] = useState(progress.accrued_seconds);
  const required = progress.required_seconds;
  const [idx, setIdx] = useState(0);
  const [completed, setCompleted] = useState(() => new Set());
  const [autoplay, setAutoplay] = useState(false);
  const [openThemes, setOpenThemes] = useState(() => new Set(lessons[0] ? [lessons[0].theme || "Conteúdo"] : []));
  const playerRef = useRef(null); const playingRef = useRef(false);
  const pendingRef = useRef(0); const accruedRef = useRef(progress.accrued_seconds);
  const lastActiveRef = useRef(Date.now());
  const [playing, setPlaying] = useState(false);
  const done = accrued >= required;

  const lesson = lessons[idx] || null;
  const isVideo = lesson?.type === "video";
  const vid = isVideo ? parseVid(lesson?.video_url) : null;
  const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("pt-BR"); } catch { return "—"; } };

  const themes = useMemo(() => {
    const out = []; const seen = {};
    lessons.forEach((l, i) => { const t = l.theme || "Conteúdo"; if (seen[t] === undefined) { seen[t] = out.length; out.push({ theme: t, items: [] }); } out[seen[t]].items.push({ ...l, index: i }); });
    return out;
  }, [lessons]);

  useEffect(() => {
    const mark = () => { lastActiveRef.current = Date.now(); };
    const evs = ["mousemove", "keydown", "scroll", "touchstart", "click", "wheel"];
    evs.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, mark));
  }, []);

  useEffect(() => {
    let destroyed = false;
    if (!vid) { playingRef.current = false; setPlaying(false); return; }
    (async () => {
      await loadYT(); if (destroyed) return;
      if (playerRef.current && playerRef.current.loadVideoById) { try { playerRef.current.loadVideoById(vid); if (autoplay) playerRef.current.playVideo(); return; } catch {} }
      playerRef.current = new window.YT.Player("yt-player", {
        videoId: vid, playerVars: { rel: 0, modestbranding: 1, autoplay: autoplay ? 1 : 0 },
        events: { onStateChange: (e) => { const p = e.data === window.YT.PlayerState.PLAYING; playingRef.current = p; setPlaying(p); } },
      });
    })();
    return () => { destroyed = true; };
  }, [vid]);

  const flush = useCallback(async () => {
    if (pendingRef.current <= 0) return;
    const secs = pendingRef.current; pendingRef.current = 0;
    try { const { data: prog } = await escolaApi.post("/escola/heartbeat", { seconds: secs }); accruedRef.current = prog.accrued_seconds; setAccrued(prog.accrued_seconds); }
    catch (e) { if (e.response?.status === 403) toast.error(e.response?.data?.detail || "Sessão encerrada"); }
  }, []);

  const reading = !isVideo && !!lesson;
  useEffect(() => {
    const iv = setInterval(() => {
      const visible = document.visibilityState === "visible";
      const under = accruedRef.current + pendingRef.current < required;
      const activeRecently = Date.now() - lastActiveRef.current < 60000;
      const counting = under && visible && ((playingRef.current && isVideo) || (reading && activeRecently));
      if (counting) pendingRef.current += 1;
      if (pendingRef.current >= 15) flush();
    }, 1000);
    const vis = () => { if (document.visibilityState === "hidden") { try { playerRef.current?.pauseVideo(); } catch {} flush(); } };
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", vis); flush(); };
  }, [required, flush, isVideo, reading]);

  useEffect(() => {
    if (!lesson) return;
    const t = setTimeout(() => setCompleted((p) => new Set(p).add(lesson.id)), 8000);
    return () => clearTimeout(t);
  }, [lesson]);

  const goto = (i) => { if (i < 0 || i >= lessons.length || !lessons[i]) return; if (lesson) setCompleted((p) => new Set(p).add(lesson.id)); setIdx(i); setOpenThemes((p) => new Set(p).add(lessons[i].theme || "Conteúdo")); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const toggleTheme = (t) => setOpenThemes((p) => { const n = new Set(p); n.has(t) ? n.delete(t) : n.add(t); return n; });

  const display = Math.min(required, accrued + pendingRef.current);
  const pct = Math.min(100, Math.round((display / required) * 100));

  return (
    <div data-testid="escola-course" className="grid grid-cols-1 gap-5 lg:grid-cols-[320px_1fr]">
      <aside className="rounded-2xl border border-white/10 bg-[#0A1C33] p-4 lg:sticky lg:top-6 lg:self-start">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-400">Conteúdo do Curso</p>
        <h3 className="mb-3 text-sm font-bold text-white">{course.nr} — {course.title}</h3>
        <div className="mb-3 rounded-xl border border-emerald-500/30 bg-slate-900/60 p-3" data-testid="escola-timer">
          <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-emerald-400" />
            <div><p className="font-mono text-base font-bold leading-none text-white">{fmt(display)}</p><p className="text-[10px] text-slate-400">de {fmt(required)} · carga horária</p></div></div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-800"><div className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-emerald-400 transition-all duration-500" style={{ width: `${pct}%` }} /></div>
          <p className="mt-1 text-right text-[10px] text-slate-400">{pct}% concluído</p>
        </div>
        <div className="space-y-1.5">
          {themes.map((th) => {
            const open = openThemes.has(th.theme);
            return (
              <div key={th.theme} className="overflow-hidden rounded-lg border border-white/5">
                <button onClick={() => toggleTheme(th.theme)} className="flex w-full items-center justify-between bg-white/5 px-3 py-2 text-left text-xs font-semibold text-slate-200 hover:bg-white/10" data-testid={`escola-theme-${th.theme}`}>
                  <span>{th.theme}</span><ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="divide-y divide-white/5">
                    {th.items.map((m) => {
                      const act = m.index === idx; const ok = completed.has(m.id);
                      return (
                        <button key={m.id} onClick={() => goto(m.index)} data-testid={`escola-module-${m.id}`}
                          className={`flex w-full items-start gap-2 px-3 py-2 text-left text-[13px] transition-all ${act ? "border-l-4 border-emerald-500 bg-emerald-500/10 font-medium text-white" : "border-l-4 border-transparent text-slate-300 hover:bg-white/5"}`}>
                          {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> : m.type === "video" ? <Video className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /> : <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />}
                          <span className="flex-1">{m.title}<span className="block text-[10px] text-slate-500">{m.type === "video" ? "Vídeo" : "Leitura"} · {m.duration_min || 0} min</span></span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      <div className="space-y-4">
        <div className="rounded-2xl border border-white/10 bg-[#0F213A] p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-400">VALBRAC · Escola Online</p>
              <h2 className="text-lg font-bold text-white">{lesson?.title || course.title}</h2>
              <p className="mt-0.5 text-xs text-slate-400">Prof. responsável: {course.responsible_name} · Início {fmtDate(enrollment.assigned_at)} · Prazo {fmtDate(enrollment.deadline_at)}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-300" data-testid="escola-autoplay">
                <input type="checkbox" checked={autoplay} onChange={(e) => setAutoplay(e.target.checked)} className="h-3.5 w-3.5 accent-emerald-500" /> Auto play
              </label>
              <Button size="sm" variant="outline" onClick={() => goto(idx - 1)} disabled={idx <= 0} className="border-white/15 bg-transparent text-slate-200 hover:bg-white/10" data-testid="escola-prev"><SkipBack className="mr-1 h-4 w-4" /> Anterior</Button>
              <Button size="sm" onClick={() => goto(idx + 1)} disabled={idx >= lessons.length - 1} className="bg-emerald-600 hover:bg-emerald-700" data-testid="escola-next">Próxima <SkipForward className="ml-1 h-4 w-4" /></Button>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs">
            {isVideo ? (playing ? <span className="flex items-center gap-1 text-emerald-400" data-testid="escola-playing"><PlayCircle className="h-4 w-4" /> Contando tempo (vídeo em reprodução)</span> : <span className="flex items-center gap-1 text-amber-400"><PauseCircle className="h-4 w-4" /> Dê play no vídeo para contar o tempo</span>)
              : <span className="flex items-center gap-1 text-emerald-400" data-testid="escola-playing"><BookOpen className="h-4 w-4" /> Lendo — o tempo conta enquanto você está nesta página</span>}
          </div>
        </div>

        {isVideo ? (
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-black shadow-xl" style={{ aspectRatio: "16/9" }}>
            {vid ? <div id="yt-player" className="h-full w-full" /> : <div className="flex h-full items-center justify-center p-6 text-center text-sm text-slate-400" data-testid="escola-no-video">Vídeo ainda não configurado para esta aula. Avise o gestor do treinamento.</div>}
          </div>
        ) : (
          <article data-testid="escola-reading" className="rounded-2xl border border-white/10 bg-[#0F213A] p-6 lg:p-8">
            <div className="text-sm leading-relaxed text-slate-200 [&_h4]:mb-2 [&_h4]:mt-5 [&_h4]:text-base [&_h4]:font-bold [&_h4]:text-emerald-300 [&_p]:mb-3 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-6 [&_strong]:text-white" dangerouslySetInnerHTML={{ __html: lesson?.content || "<p>Conteúdo em preparação.</p>" }} />
          </article>
        )}

        <div className="rounded-2xl border border-white/10 bg-[#0F213A] p-5 text-center">
          {done ? (
            <Button onClick={() => reload("exam")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="escola-start-exam"><Award className="mr-2 h-4 w-4" /> Fazer prova final</Button>
          ) : (
            <p className="text-sm text-slate-400" data-testid="escola-exam-locked">A prova será liberada após completar as 8 horas ({fmt(required - display)} restantes).</p>
          )}
        </div>
      </div>
    </div>
  );
}

function IntroStep({ data, onStart }) {
  const { course } = data;
  const items = [
    ["Carga horária de 8 horas", "O tempo é contado enquanto você assiste ao vídeo OU lê o material, com a página aberta."],
    ["Pode pausar e retomar", "Volte de onde parou quando quiser — você tem 7 dias para concluir o curso."],
    ["Avaliação final", `Ao completar as 8h, faça a prova de ${course.questions.length} questões; são necessários ${course.pass_score} acertos. Se reprovar, pode refazer.`],
    ["Certificado", "Após a aprovação, baixe seu certificado de conclusão em PDF."],
    ...(IS_MOBILE ? [["Registro de presença", "Pelo celular, sua foto é registrada a cada acesso, apenas para fins de auditoria."]] : []),
  ];
  return (
    <div className="mx-auto max-w-2xl rounded-2xl bg-white p-6 text-slate-800 shadow-xl" data-testid="escola-intro">
      <div className="mb-1 flex items-center gap-2 text-[#0B2545]"><School className="h-5 w-5" /><h2 className="text-lg font-bold">Antes de começar — {course.nr}</h2></div>
      <p className="text-sm font-semibold text-[#0B2545]">{course.title}</p>
      <p className="mt-1 text-sm text-slate-600">{course.summary}</p>
      <div className="mt-4 space-y-2">
        {items.map(([t, d], i) => (
          <div key={i} className="flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50/60 p-3" data-testid={`intro-item-${i}`}>
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
            <div><p className="text-sm font-semibold text-slate-700">{t}</p><p className="text-xs text-slate-500">{d}</p></div>
          </div>
        ))}
      </div>
      <p className="mt-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-700">Dica: mantenha esta aba aberta e o vídeo rodando. Se trocar de aba ou pausar, o cronômetro para automaticamente.</p>
      <Button onClick={onStart} className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700" data-testid="escola-start"><PlayCircle className="mr-2 h-4 w-4" /> Iniciar treinamento</Button>
    </div>
  );
}

export default function StudentPortal() {
  const [phase, setPhase] = useState("loading"); // loading, login, face, intro, course, exam, result
  const [loginInfo, setLoginInfo] = useState(null);
  const [data, setData] = useState(null);
  const [result, setResult] = useState(null);

  const loadData = useCallback(async () => {
    const { data } = await escolaApi.get("/escola/me");
    setData(data);
    return data;
  }, []);

  const afterAuth = useCallback(async () => {
    if (IS_MOBILE) { setPhase("face"); return; }
    try {
      const { data } = await escolaApi.post("/escola/skip-face");
      setEscolaToken(data.token);
      await loadData();
      setPhase("intro");
    } catch { setPhase("face"); }
  }, [loadData]);

  const boot = useCallback(async () => {
    const key = new URLSearchParams(window.location.search).get("k");
    if (key) {
      try {
        const { data } = await escolaApi.post("/escola/login-key", { key });
        setEscolaToken(data.token);
        window.history.replaceState({}, "", window.location.pathname);
        setLoginInfo(data); await afterAuth();
        return;
      } catch {
        window.history.replaceState({}, "", window.location.pathname);
        clearEscolaToken(); setPhase("login"); return;
      }
    }
    try {
      const d = await loadData();
      if (String(d.enrollment.status).includes("Aprovado")) { setResult({ passed: true, score: d.enrollment.best_score, total: d.course.questions.length, pass_score: d.course.pass_score }); setPhase("result"); }
      else setPhase("course");
    } catch { clearEscolaToken(); setPhase("login"); }
  }, [loadData]);

  useEffect(() => { boot(); }, [boot]);

  if (phase === "loading") return <Shell><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-emerald-400" /></div></Shell>;

  return (
    <Shell wide={phase === "course"}>
      {phase === "login" && <LoginStep onLogged={(info) => { setLoginInfo(info); afterAuth(); }} />}
      {phase === "face" && <FaceStep info={loginInfo} onDone={async () => { await loadData(); setPhase("intro"); }} />}
      {phase === "intro" && data && <IntroStep data={data} onStart={() => setPhase("course")} />}
      {phase === "course" && data && <CourseStep data={data} reload={(p) => setPhase(p)} />}
      {phase === "exam" && data && <ExamStep course={data.course} onResult={(r) => { setResult(r); setPhase("result"); }} />}
      {phase === "result" && result && (
        <div className="mx-auto max-w-md rounded-2xl bg-white p-8 text-center text-slate-800 shadow-xl" data-testid="escola-result">
          {result.passed ? <CheckCircle2 className="mx-auto h-16 w-16 text-emerald-500" /> : <XCircle className="mx-auto h-16 w-16 text-rose-500" />}
          <h2 className="mt-3 text-xl font-bold text-[#0B2545]">{result.passed ? "Aprovado!" : "Não aprovado"}</h2>
          <p className="mt-1 text-sm text-slate-500">Você acertou {result.score} de {result.total} (mínimo {result.pass_score}).</p>
          {result.passed ? (
            <a href={certificateUrl()} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700" data-testid="escola-certificate">
              <Award className="mr-2 h-4 w-4" /> Baixar certificado
            </a>
          ) : (
            <Button onClick={() => { setResult(null); setPhase("exam"); }} className="mt-5 bg-[#0B2545] hover:bg-[#134074]" data-testid="escola-retry"><RotateCcw className="mr-2 h-4 w-4" /> Refazer prova</Button>
          )}
        </div>
      )}
    </Shell>
  );
}
